
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowPathIcon, ChartBarIcon, ChartPieIcon, CpuChipIcon, CurrencyEuroIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Panel } from '../../components/panels/Panel';
import { DEMO_CONFIG, DEMO_PERIODS, DEMO_PRODUCTS, DEMO_STATIONS } from '../../data/perception-demo-data';
import { formatDemoDate, getPerceptionDemoReport } from '../../services/dashboard/perception-demo-service';
import { PerceptionDemoOverview } from './perception-demo-components/PerceptionDemoOverview';
import { PerceptionDemoWorkstations } from './perception-demo-components/PerceptionDemoWorkstations';
import { PerceptionDemoQuality } from './perception-demo-components/PerceptionDemoQuality';
import { PerceptionDemoAI } from './perception-demo-components/PerceptionDemoAI';
import { PerceptionDemoROI } from './perception-demo-components/PerceptionDemoROI';

const MENU_ITEMS = [
  { path: 'overview', label: 'Overview', title: 'OVERVIEW', Icon: Squares2X2Icon },
  { path: 'workstations', label: 'Workstations', title: 'WORKSTATIONS', Icon: ChartBarIcon },
  { path: 'quality', label: 'Quality & defects', title: 'QUALITY & DEFECTS', Icon: ChartPieIcon },
  { path: 'ai-performance', label: 'AI performance', title: 'AI PERFORMANCE', Icon: CpuChipIcon },
  { path: 'business-impact', label: 'Business impact', title: 'BUSINESS IMPACT', Icon: CurrencyEuroIcon },
];

const DEFAULT_SCOPE = { period: '30d', station: 'all', product: 'all' };

const DemoFilters = ({ scope, onChange, report }) => (
  <Panel noHeader rounded category="secondary" height="h-auto" overflowAuto={false}>
    <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div className="flex flex-wrap gap-4">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-perception-gray-600">Period</div>
          <div className="flex flex-wrap gap-1 rounded-md bg-perception-black-700 p-1">
            {DEMO_PERIODS.map(({ id, label }) => (
              <button key={id} type="button" onClick={() => onChange({ ...scope, period: id })} className={clsx('rounded px-3 py-2 text-xs transition-colors', scope.period === id ? 'bg-perception-blue font-semibold text-white' : 'text-perception-gray-300 hover:bg-perception-gray-800')}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor="demo-station" className="mb-2 block text-xs font-semibold uppercase text-perception-gray-600">Workstation</label>
          <select id="demo-station" value={scope.station} onChange={(event) => onChange({ ...scope, station: event.target.value })} className="min-w-[165px] rounded-md border border-perception-gray-700 bg-perception-black-700 px-3 py-2.5 text-xs text-perception-gray-100 outline-none focus:border-perception-blue">
            <option value="all">All workstations</option>
            {DEMO_STATIONS.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="demo-product" className="mb-2 block text-xs font-semibold uppercase text-perception-gray-600">Watch model</label>
          <select id="demo-product" value={scope.product} onChange={(event) => onChange({ ...scope, product: event.target.value })} className="min-w-[165px] rounded-md border border-perception-gray-700 bg-perception-black-700 px-3 py-2.5 text-xs text-perception-gray-100 outline-none focus:border-perception-blue">
            <option value="all">All models</option>
            {DEMO_PRODUCTS.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-perception-gray-600">{formatDemoDate(report.scope.start)} – {formatDemoDate(report.scope.end)}</span>
        <button type="button" onClick={() => onChange({ ...DEFAULT_SCOPE })} className="flex items-center gap-1.5 rounded-md bg-perception-gray-800 px-3 py-2 text-xs text-perception-gray-100 transition-colors hover:bg-perception-gray-700"><ArrowPathIcon className="size-4"/> Reset</button>
      </div>
    </div>
  </Panel>
);

export const PerceptionDemoDashboard = () => {
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [dashboardHeight, setDashboardHeight] = useState(null);
  const dashboardRef = useRef(null);
  const contentRef = useRef(null);
  const location = useLocation();
  const report = useMemo(() => getPerceptionDemoReport(scope), [scope]);
  const activeItem = MENU_ITEMS.find(({ path }) => location.pathname.endsWith(`/${path}`)) ?? MENU_ITEMS[0];

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!dashboardRef.current) return;

      // Limit the dashboard to the visible space below the application's header.
      const top = dashboardRef.current.getBoundingClientRect().top;
      const height = Math.max(0, Math.floor(window.innerHeight - top));
      setDashboardHeight((currentHeight) => currentHeight === height ? currentHeight : height);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  return (
    <div ref={dashboardRef} className="flex min-h-0 overflow-hidden bg-perception-black-900" style={{ height: dashboardHeight === null ? '100%' : `${dashboardHeight}px`, maxHeight: '100dvh' }}>
      <Panel noHeader noPadding category="main" width="w-52" overflowAuto={false} className="min-h-0 shrink-0 border-r border-perception-black-500">
        <div className="flex h-full min-h-0 flex-col overflow-y-auto p-3">
          <div className="px-2 pb-3 pt-4 text-xs font-semibold uppercase tracking-wider text-perception-gray-600">Dashboard</div>
          <nav className="flex flex-col gap-1">
            {MENU_ITEMS.map(({ path, label, Icon }) => (
              <NavLink key={path} to={path} className={({ isActive }) => clsx('flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors', isActive ? 'bg-perception-black-500 font-semibold text-white' : 'text-perception-gray-400 hover:bg-perception-black-700 hover:text-white')}>
                <Icon className="size-4 shrink-0"/>
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </Panel>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <Panel title={activeItem.title} noPadding overflowAuto={false} className="min-h-0">
          <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="mx-auto flex w-full max-w-[1580px] flex-col gap-4 px-4 py-7 lg:px-8 2xl:px-12">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-perception-gray-100">{DEMO_CONFIG.plantName}</div>
                  <div className="mt-1 text-xs text-perception-gray-600">{DEMO_CONFIG.workshopName} · Connected operations intelligence</div>
                </div>
              </div>
              <DemoFilters scope={scope} onChange={setScope} report={report}/>
              <Routes>
                <Route index element={<Navigate to="overview" replace/>}/>
                <Route path="overview" element={<PerceptionDemoOverview report={report}/>}/>
                <Route path="workstations" element={<PerceptionDemoWorkstations report={report}/>}/>
                <Route path="quality" element={<PerceptionDemoQuality report={report}/>}/>
                <Route path="ai-performance" element={<PerceptionDemoAI report={report}/>}/>
                <Route path="business-impact" element={<PerceptionDemoROI report={report}/>}/>
                <Route path="*" element={<Navigate to="overview" replace/>}/>
              </Routes>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};
